/* eslint-disable react-hooks/exhaustive-deps */
import * as THREE from 'three';
import React, { useMemo, useState } from 'react';
import { useThree } from '@react-three/fiber';

import { const_var, params } from '../../../../../../redux/reducers/reducer';

import { delModel, extrudeSettings, roofPattern, textureAnisotropy, wainscotTrimModel } from '../../../../../../utils/Functions';
import { CSG } from '../../../../../../assets/libs/csg-lib';
import { MaterialOuterWallNew, MaterialWainScotTrim, MaterialWainScotTrimNew, MaterialWainScotWallNew } from '../../../../CustomMaterial';
import { createSidingTextureObject } from '../../../../../../redux/reducers/utils';


const OutsideWallModel = () => {
    const { gl } = useThree();

    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement != undefined && canvasElement[0]?.clientWidth > 1050 && !params.p_v_w) ? true : false
    const [wallHeight, setWallHeight] = useState(params.p_h);
    
    const offsetWithDoorThickness = 0.145

    const wallModel = useMemo(() => {
        let model = new THREE.Shape();

        model.moveTo(-(params.p_d / 2 + offsetWithDoorThickness), wallHeight - 0.02);
        model.lineTo(params.p_d / 2 + offsetWithDoorThickness, wallHeight - 0.02);
        model.lineTo(params.p_d / 2 + offsetWithDoorThickness, 0.01);
        model.lineTo(-(params.p_d / 2 + offsetWithDoorThickness), 0.01);
        model.closePath()

        return model;
    
    }, [params.p_d, wallHeight, params.p_w_c_n])

    useMemo(() => {
        let overHeight = 0;
        const leftHeight = (params.singleSlope && params.freeStandingSide === "left") ? params.p_h - ((params.p_r_p / 12) * params.p_w) :  params.p_h;
        if (parseInt(params.cB_addStorage_right) < params.p_w / 2  && !params.singleSlope) {
            if (params.p_r_s === 1) {
                overHeight = (parseInt(params.cB_addStorage_right)) * params.p_r_p / 12 + const_var.roofRegularDst * 2 - 0.0175;
            } else {
                overHeight = (parseInt(params.cB_addStorage_right) + 0.0075) * params.p_r_p / 12;
            }
        } else {
            if (params.p_r_s === 1) {
                overHeight = (params.p_w - parseInt(params.cB_addStorage_right)) * params.p_r_p / 12 + const_var.roofRegularDst * 2 - 0.0175;
            } else {
                overHeight = (params.p_w - parseInt(params.cB_addStorage_right) + 0.0075) * params.p_r_p / 12;
            }
        }

        setWallHeight(leftHeight + overHeight);
    
    }, [params.cB_addStorage_right, params.p_r_s, params.p_r_p, params.p_w, params.singleSlope, params.freeStandingSide, params.p_h])


    const mainWallModel = useMemo(() => {
        /************** cutting hole using CSG ***************/
        let verticalHorizontalPos = {"x": params.p_w / 2 - parseInt(params.cB_addStorage_right) - 0.005,"y" : (params.p_w_c_n ? 2.935 : 0), "z" : params.p_d / -2 - offsetWithDoorThickness}
        let verticalHorizontalRot = {"x":0,"y" : 0,"z" : Math.PI / 2}
        let patternLength = wallHeight + (params.p_w_c_n ? -2.935 : 0)
        let patternDepth = params.p_d + offsetWithDoorThickness * 2  
        
        const meshWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength, isPattern), extrudeSettings(patternDepth)))
        const meshDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('rightStorageFront', 'wall'), extrudeSettings(1)))
        const meshWallDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(wallModel, extrudeSettings(1)))
        const meshFrontOffsetWithDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('front', 'wall'), extrudeSettings(1)))
        const meshBackOffsetWithDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('back', 'wall'), extrudeSettings(1)))

        let meshWallModel_Temp = meshWallModel.clone();
        meshWallModel_Temp.position.set(verticalHorizontalPos.x,verticalHorizontalPos.y,verticalHorizontalPos.z);
        meshWallModel_Temp.rotation.set(verticalHorizontalRot.x,verticalHorizontalRot.y,verticalHorizontalRot.z);
        meshWallModel_Temp.updateMatrixWorld();

        let meshWallDelModel_Temp = meshWallDelModel.clone();
        meshWallDelModel_Temp.position.set(params.p_w / 2 - parseInt(params.cB_addStorage_right) + 0.5, 0, 0);
        meshWallDelModel_Temp.rotation.set(0,Math.PI / -2,0)
        meshWallDelModel_Temp.updateMatrixWorld();

        let meshDelModel_Temp = meshDelModel.clone();
        meshDelModel_Temp.position.set(params.p_w / 2 - parseInt(params.cB_addStorage_right) -0.5, 0, 0);
        meshDelModel_Temp.rotation.set(0,Math.PI / 2,0)
        meshDelModel_Temp.updateMatrixWorld();

        let meshFrontOffsetWithDoorDelModel_Temp = meshFrontOffsetWithDoorDelModel.clone();
        meshFrontOffsetWithDoorDelModel_Temp.position.set(0, 0, params.p_d / 2 - 0.05);
        meshFrontOffsetWithDoorDelModel_Temp.updateMatrixWorld()

        let meshBackOffsetWithDoorDelModel_Temp = meshBackOffsetWithDoorDelModel.clone();
        meshBackOffsetWithDoorDelModel_Temp.position.set(0, 0, -(params.p_d / 2 + 0.95));
        meshBackOffsetWithDoorDelModel_Temp.updateMatrixWorld()

        const meshWallModel_CSG = CSG.fromMesh(meshWallModel_Temp);
        const meshDelModel_CSG = CSG.fromMesh(meshDelModel_Temp);
        const meshWallDelModel_CSG = CSG.fromMesh(meshWallDelModel_Temp);
        const meshFrontOffsetWithDoorDelModel_CSG = CSG.fromMesh(meshFrontOffsetWithDoorDelModel_Temp);
        const meshBackOffsetWithDoorDelModel_CSG = CSG.fromMesh(meshBackOffsetWithDoorDelModel_Temp);


        const wallModel_CSG =  meshWallModel_CSG.subtract(meshDelModel_CSG);
        const meshWallDel_CSG = wallModel_CSG.intersect(meshWallDelModel_CSG);
        const frontWallDel_CSG = meshWallDel_CSG.subtract(meshFrontOffsetWithDoorDelModel_CSG);
        const backWallDel_CSG = frontWallDel_CSG.subtract(meshBackOffsetWithDoorDelModel_CSG);
        const CSGWallModel = CSG.toMesh(backWallDel_CSG,new THREE.Matrix4(),)

        /************** cutting hole in wainscot using CSG ***************/
        let patternDepthWS = params.p_d + offsetWithDoorThickness * 2  
        let wainscotVerticalHorizontalPos = {"x": params.p_w / 2 - parseInt(params.cB_addStorage_right) - 0.005,"y" : 0, "z" : params.p_d / -2 - offsetWithDoorThickness}
        let wainscotVerticalHorizontalRot = {"x":0,"y" : 0,"z" : Math.PI / 2}
        let lengthPattern = 3
        let depthPattern = patternDepthWS

        const meshWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPattern,isPattern && params.wainscotTexture == "none"), extrudeSettings(depthPattern)))
        let meshWainscotModel_Temp = meshWainscotModel.clone();
        meshWainscotModel_Temp.position.set(wainscotVerticalHorizontalPos.x,wainscotVerticalHorizontalPos.y,wainscotVerticalHorizontalPos.z);
        meshWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRot.x,wainscotVerticalHorizontalRot.y,wainscotVerticalHorizontalRot.z);
        meshWainscotModel_Temp.updateMatrixWorld();

        const meshWainscotModel_CSG = CSG.fromMesh(meshWainscotModel_Temp);

        const wainscotModel_CSG = meshWainscotModel_CSG.subtract(meshDelModel_CSG);
        const wainscotModelCSG = wainscotModel_CSG.intersect(meshWallDelModel_CSG);
        const wainscotCSG = wainscotModelCSG.subtract(meshFrontOffsetWithDoorDelModel_CSG);
        const wainscotDelCSG = wainscotCSG.subtract(meshBackOffsetWithDoorDelModel_CSG);
        const CSGWainscotModel = CSG.toMesh(
            wainscotDelCSG,
            new THREE.Matrix4()
        )

        /************** cutting hole in Wainscot Trim using CSG ***************/

        const meshWainscotTrimModel = new THREE.Mesh(new THREE.ExtrudeGeometry(wainscotTrimModel(0.14, 0.17), extrudeSettings(params.p_d + offsetWithDoorThickness * 2)))
        let meshWainscotTrimModel_Temp = meshWainscotTrimModel.clone();
        meshWainscotTrimModel_Temp.position.set(params.p_w / 2 - parseInt(params.cB_addStorage_right) + 0.0025, 3, params.p_d / 2 + offsetWithDoorThickness);
        meshWainscotTrimModel_Temp.rotateY(Math.PI)
        meshWainscotTrimModel_Temp.updateMatrixWorld();

        const meshWainscotTrimModel_CSG = CSG.fromMesh(meshWainscotTrimModel_Temp);

        const wainscotTrimModel_CSG = meshWainscotTrimModel_CSG.subtract(meshDelModel_CSG);
        const wainscotTrimModelCSG = wainscotTrimModel_CSG.subtract(meshFrontOffsetWithDoorDelModel_CSG);
        const wainscotTrimDelModelCSG = wainscotTrimModelCSG.subtract(meshBackOffsetWithDoorDelModel_CSG);
        const CSGWainscotTrimModel = CSG.toMesh(
            wainscotTrimDelModelCSG,
            new THREE.Matrix4()
        )

        return {
            wall: CSGWallModel.geometry,
            wainscotWall: CSGWainscotModel.geometry,
            wTrim: CSGWainscotTrimModel.geometry
        }
        /****************************************** *********/
    }, [params.wallTexture,params.wainscotTexture,params.p_v_w, params.p_w, params.cB_addStorage_right, params.p_d, wallHeight, wallModel, const_var.newDoorsArray, params.p_w_c_n,isPattern])

    const mainColor = useMemo(() => params.p_w_c.replace('0x', '#'), [params.p_w_c]);
    
     

    return (
        <group onPointerOver={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()} >
            {mainWallModel.wall &&
                <mesh name="c_b_r_s_l_w" geometry={mainWallModel.wall}>
                    <MaterialOuterWallNew isPattern={isPattern}   visibleSection={"center"} />
                </mesh>
            }
            {params.p_w_c_n === true && params.cB_addStorage_check_right && mainWallModel.wainscotWall &&
                <mesh name="c_b_r_s_l_w_wainscot" geometry={mainWallModel.wainscotWall} position={[ - 0.0012, 0, 0]} >
                    <MaterialWainScotWallNew isPattern={isPattern}   visibleSection={"center"} />
                </mesh>
            }
            {params.p_w_c_n === true && params.cB_addStorage_check_right && mainWallModel.wTrim &&
                <mesh name="wainscotTrim" geometry={mainWallModel.wTrim}>
                    <MaterialWainScotTrimNew visibleSection={"center"}  />
                </mesh>
            }
        </group>
    )
}

export default OutsideWallModel